import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Projectile } from "../entities/Projectile.ts";
import type { Vec2 } from "../core/types.ts";
import { TowerRenderer, type TowerVisualEffects } from "./TowerRenderer.ts";
import { EnemyRenderer } from "./EnemyRenderer.ts";
import { ProjectileRenderer } from "./ProjectileRenderer.ts";
import type { BossPathColor } from "./BossPathColor.ts";

/**
 * Facade: delegates to TowerRenderer, EnemyRenderer, ProjectileRenderer.
 * Maintains the same public API for backward compatibility.
 */
export class EntityRenderer {
  private towerRenderer: TowerRenderer;
  private enemyRenderer: EnemyRenderer;
  private projectileRenderer: ProjectileRenderer;
  private frameTime = 0;
  // Task-009 (O-10 legibility, D-324) — reused scratch for the live Jammer
  // hosts (tower→source tether). Persisted + length-reset each frame so the
  // render hot path allocates no per-frame array (mirrors EnemyRenderer's
  // `auraHostsScratch` for the Overseer's aura→host tether).
  private readonly jammerHostsScratch: Enemy[] = [];

  constructor(ctx: CanvasRenderingContext2D, tileSize: number) {
    this.towerRenderer = new TowerRenderer(ctx, tileSize);
    this.enemyRenderer = new EnemyRenderer(ctx);
    this.projectileRenderer = new ProjectileRenderer(ctx);
  }

  /** Set frame timestamp once per frame (CR-15) */
  setTime(time: number): void {
    this.frameTime = time;
  }

  /** Set active skill visual effects for tower rendering */
  set towerEffects(fx: TowerVisualEffects) {
    this.towerRenderer.effects = fx;
  }

  set showAllHPBars(val: boolean) {
    this.enemyRenderer.showAllHPBars = val;
  }

  /**
   * @param enemies — Task-009 (O-10, D-324): optional live-enemy list, scanned
   *  once for Jammer hosts so `TowerRenderer` can tether a suppressed tower
   *  back to its source. Omitted (default `[]`) draws exactly as before.
   */
  renderTowers(towers: Tower[], enemies: Enemy[] = []): void {
    const jammerHosts = this.jammerHostsScratch;
    jammerHosts.length = 0;
    for (const e of enemies) {
      if (e.active && e.special?.type === "jammer") jammerHosts.push(e);
    }
    this.towerRenderer.render(towers, this.frameTime, jammerHosts);
  }

  renderEnemies(enemies: Enemy[]): void {
    this.enemyRenderer.render(enemies, this.frameTime);
  }

  /** Seed a render-only death-pop for a killed enemy (BL-36). Wired to the
   *  `enemy.killed` event in main.ts; delegates to the EnemyRenderer. */
  spawnDeathPop(enemy: Enemy): void {
    this.enemyRenderer.spawnDeathPop(enemy);
  }

  renderProjectiles(projectiles: Projectile[]): void {
    this.projectileRenderer.render(projectiles);
  }

  /** CR-H5 (LD-22.1-4): opacity multiplier so caller can dim non-primary
   *  paths (breach secondaries) below the primary canonical color.
   *  CR-P3 (Bug D): drawPackets=false suppresses animated data packets
   *  for non-primary paths to avoid visual packet swarm overlay. */
  renderPath(
    pathPixels: Vec2[],
    opacity = 1.0,
    drawPackets = true,
    bossColor?: BossPathColor,
  ): void {
    this.projectileRenderer.renderPath(
      pathPixels,
      this.frameTime,
      opacity,
      drawPackets,
      bossColor,
    );
  }
}
